/**
 * EditorGuideContext - Context for providing GuideService to editor components
 *
 * Provides guideService instance to TextElement, StyleFieldRow, and other
 * editor components that need to display contextual help via WithGuideHelp.
 *
 * Usage:
 * ```tsx
 * // In parent component (e.g., OnboardingHeaderSection):
 * const guideService = useMemo(() => new GuideService(), []);
 *
 * <EditorGuideProvider guideService={guideService}>
 *   <TextElement ... />
 * </EditorGuideProvider>
 *
 * // In child component (e.g., TextElement):
 * const guideService = useEditorGuideService();
 * ```
 */

import React, { createContext, useContext, useMemo } from 'react';
import { GuideService } from '@modules/firebase-core/services/GuideService';

// Context type
type EditorGuideContextType = GuideService | null;

// Create context with null default
const EditorGuideContext = createContext<EditorGuideContextType>(null);

/**
 * Props for EditorGuideProvider
 */
export interface EditorGuideProviderProps {
  /** GuideService instance to provide */
  guideService?: GuideService;
  /** Children components */
  children: React.ReactNode;
}

/**
 * Provider component for EditorGuideContext
 *
 * If no guideService is provided, creates a new instance automatically.
 */
export const EditorGuideProvider: React.FC<EditorGuideProviderProps> = ({
  guideService: providedService,
  children,
}) => {
  // Create default guideService if not provided
  const guideService = useMemo(
    () => providedService ?? new GuideService(),
    [providedService]
  );

  return (
    <EditorGuideContext.Provider value={guideService}>
      {children}
    </EditorGuideContext.Provider>
  );
};

/**
 * Hook to access GuideService from context
 *
 * @returns GuideService instance or null if not in provider
 */
export const useEditorGuideService = (): GuideService | null => {
  return useContext(EditorGuideContext);
};

/**
 * Hook to access GuideService with required check
 *
 * @throws Error if used outside EditorGuideProvider
 * @returns GuideService instance
 */
export const useRequiredEditorGuideService = (): GuideService => {
  const guideService = useContext(EditorGuideContext);
  if (!guideService) {
    throw new Error('useRequiredEditorGuideService must be used within EditorGuideProvider');
  }
  return guideService;
};

export { EditorGuideContext };
